/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { EmployeeModel } from '../../../../models/employee.model';
import { TaskModel } from '../../../../models/task.model';
import {
  BarChartDatasetsModel,
  BarChartModel,
  DistributionGraphModel
} from '../../../../models/timetracking.model';
import { WorkflowModel } from '../../../../models/workflow.model';
import { EmployeeService } from '../../../../services/employee.service';
import { TaskService } from '../../../../services/task.service';
import { TimeTrackingService } from '../../../../services/time-tracking.service';
import { WorkflowService } from '../../../../services/workflow.service';

@Component({
  selector: 'app-workflow-dashboard',
  templateUrl: './workflow-dashboard.component.html',
  styleUrls: ['./workflow-dashboard.component.scss'],
  standalone: false
})
export class WorkflowDashboardComponent implements OnInit, OnDestroy {
  workflowChangedSubscription: Subscription = new Subscription();
  tasksChangedSubscription: Subscription = new Subscription();
  // page properties
  pageTitle: string = '';
  workflowId: string = '';
  workflow!: WorkflowModel;
  // cards
  totalTime: number = 0;
  numberOfEmployees: number = 0;
  numberOfTasks: number = 0;
  numberOfCheckpoints: number = 0;
  precision: number = 5;
  loading: boolean = true;

  // table
  displayedColumns: string[] = ['name', 'numberOfTasks', 'totalTime', 'medianTime'];

  //distribution chart properties
  checkpointName: string = ''; // ng model
  distributionGraphDataObject!: DistributionGraphModel;
  //Bar chart properties
  barChartDataObject!: BarChartModel;

  //Task and employee properties
  tasks: TaskModel[] = [];
  workflowTasks: TaskModel[] = [];
  allEmployees: EmployeeModel[] = [];
  executes: string[] = [];

  employeeList: {
    name: string;
    numberOfTasks: number;
    totalTime: number;
    medianTime: number;
  }[] = [];

  taskList: {
    taskID: string;
    executedBy: string[];
    timeTaken: number;
  }[] = [];

  checkPointList: {
    name: string;
    timeTaken: number[];
    medianTime: number;
  }[] = [];

  @ViewChild(MatTable)
  table!: MatTable<object>;

  constructor(
    private route: ActivatedRoute,
    private workflowService: WorkflowService,
    private employeeService: EmployeeService,
    private taskService: TaskService,
    private timeTrackingService: TimeTrackingService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(async (params) => {
      this.loading = true;
      this.checkPointList = [];
      this.taskList = [];
      this.employeeList = [];
      this.allEmployees = [];
      this.workflowTasks = [];
      this.totalTime = 0;
      this.numberOfCheckpoints = 0;
      this.tasks = [];
      this.executes = [];

      // get workflow data
      this.workflowId = params.workflowId;
      this.workflow = await this.workflowService.getWorkflow(this.workflowId);
      // let filterStart = this.getIsoStringFromDateAndTimeString(
      //   new Date(params.fromDate),
      //   params.fromTime
      // );
      // let filterEnd = this.getIsoStringFromDateAndTimeString(new Date(params.toDate), params.toTime);
      // search functions
      this.tasks = await this.getTasks(this.workflowId, params.fromDate, params.toDate);
      this.tasks.forEach((task: TaskModel) => {
        if (task.workflowId == this.workflowId) {
          if (!this.workflowTasks.includes(task)) {
            this.workflowTasks.push(task);
          }
        }
      });
      this.workflowTasks.forEach((task: TaskModel) => {
        task.executedBy.forEach((id: string) => {
          if (!this.executes.includes(id)) {
            this.executes.push(id);
          }
        });
      });
      this.allEmployees = await this.getEmployees();

      //after we got the data stop the loading
      this.loading = false;

      if (this.workflow) {
        this.pageTitle = this.workflow.name;
        this.fillCheckpoints();
        this.fillEmployeeListData();

        // for cards
        this.numberOfEmployees = this.employeeList.length;
        this.numberOfTasks = this.workflowTasks.length;

        //distribution chart
        this.distributionGraphDataObject = this.timeTrackingService.createDistributionGraph(
          this.taskList.map((x) => x.timeTaken)
        );

        //Bar chart
        let barChartArray: string[] = this.checkPointList.map((x) => x.medianTime.toPrecision(2));
        let barChartDatasets: BarChartDatasetsModel[] = [
          {
            label: $localize`:@@medianTime:Median Time`,
            backgroundColor: '#4A7563',
            data: barChartArray,
            maxBarThickness: 100
          }
        ];
        this.barChartDataObject = this.timeTrackingService.createBarChart(
          this.checkPointList.map((x) => x.name),
          barChartDatasets
        );
        this.cdr.detectChanges();
      }
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy() {
    this.workflowChangedSubscription.unsubscribe();
    this.tasksChangedSubscription.unsubscribe();
  }

  // distribution graph methods >>
  changeDistributionGraph(checkpointName: string) {
    if (checkpointName === '') {
      this.distributionGraphDataObject = this.timeTrackingService.createDistributionGraph(
        this.taskList.map((x) => x.timeTaken)
      );
    } else {
      this.checkpointName = checkpointName;
      // here we must get the arr depend on the checkpointID
      let checkpoint = this.checkPointList.find((x) => x.name === checkpointName);
      let checkInd = this.checkPointList.indexOf(checkpoint!);
      let distGraphArray2 = this.checkPointList[checkInd]?.timeTaken;
      this.distributionGraphDataObject =
        this.timeTrackingService.createDistributionGraph(distGraphArray2);
    }
  }

  // getIsoStringFromDateAndTimeString(date: Date, time: string): string {
  //   let timeParts = time.split(':');
  //   let hours = parseInt(timeParts[0]);
  //   let minutes = parseInt(timeParts[1]);

  //   date.setHours(hours);
  //   date.setMinutes(minutes);
  //   date.setSeconds(0);
  //   date.setMilliseconds(0);

  //   return date.toISOString();
  // }

  async getTasks(workflowId: string, filterStart: string, filterEnd: string): Promise<TaskModel[]> {
    return (
      await this.taskService.selectTasks(
        {
          search_criteria: {
            status: { $eq: 'CLOSED' }
          },
          skip: 0,
          limit: 0,
          sort: 'name',
          select: 'task_id name assigned_to workflow_id executed_by claimed_by time_tracking'
        },
        filterStart,
        filterEnd
      )
    ).body;
  }
  getEmployees(): Promise<EmployeeModel[]> {
    return this.employeeService._searchEmployees({
      search_criteria: {
        _id: {
          $in: this.executes
        }
      },
      sort: 'full_name',
      select: '_id full_name',
      skip: 0,
      limit: 0
    });
  }

  fillCheckpoints() {
    for (let task of this.workflowTasks) {
      if (task.time_tracking && task.time_tracking.length >= 2) {
        let record = {
          taskID: task.taskId,
          executedBy: task.executedBy,
          timeTaken: 0
        };
        let start: Date;
        let end: Date;
        for (let i = 0; i < task.time_tracking?.length!; i++) {
          let check = task.time_tracking![i];
          if (check.name === 'start') {
            start = check.start;
          } else if (check.name === 'end') {
            end = check.start;
          } else {
            let curr = check.start;
            let next = end!;
            let span;
            if (i + 1 < task.time_tracking?.length!) {
              next = task.time_tracking![i + 1].start;
              span = (Date.parse(next.toString()) - Date.parse(curr.toString())) / 3600000;
              if (!this.checkPointList.map((x) => x.name).includes(check.name)) {
                let point: {
                  name: string;
                  timeTaken: number[];
                  medianTime: number;
                } = {
                  name: check.name,
                  timeTaken: [],
                  medianTime: 0
                };
                point.name = check.name;
                point.timeTaken.push(span);
                this.checkPointList.push(point);
              } else {
                let checkpoint = this.checkPointList.find((x) => x.name === check.name);
                let checkInd = this.checkPointList.indexOf(checkpoint!);
                this.checkPointList[checkInd].timeTaken.push(span);
              }
            } else {
              end = check.start;
            }
            this.numberOfCheckpoints++;
          }
        }
        let diff = (Date.parse(end!.toString()) - Date.parse(start!.toString())) / 3600000;
        record.timeTaken = diff;
        this.taskList.push(record);
        this.totalTime += diff;
      }
    }
    // get median for bar chart
    for (let i = 0; i < this.checkPointList.length; i++) {
      this.checkPointList[i].medianTime = this.timeTrackingService.median(
        this.checkPointList[i].timeTaken
      );
    }
  }
  fillEmployeeListData() {
    let timeTaken: number[] = [];
    for (let employee of this.allEmployees) {
      let record = {
        name: employee.name,
        numberOfTasks: 0,
        totalTime: 0,
        medianTime: 0
      };
      for (let task of this.taskList) {
        if (task.executedBy.includes(employee.employeeId)) {
          record.numberOfTasks++;
          timeTaken.push(task.timeTaken);
          record.totalTime += task.timeTaken;
        }
      }
      record.medianTime = this.timeTrackingService.median(timeTaken);
      this.employeeList.push(record);
    }
    this.table.renderRows();
  }
}
